import React from 'react';
import { Table as JoyTable, Sheet } from '@mui/joy';
import { cn } from '@utils/cn';

interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (value: any, row: T) => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  className?: string;
  striped?: boolean;
  hover?: boolean;
  stickyHeader?: boolean;
  loading?: boolean;
  emptyMessage?: string;
}

export function Table<T extends Record<string, any>>({
  columns,
  data,
  className,
  striped = false,
  hover = true,
  stickyHeader = false,
  loading = false,
  emptyMessage = 'No data available',
}: TableProps<T>) {
  const getValue = (row: T, key: string) => {
    const keys = key.split('.');
    let value: any = row;
    for (const k of keys) {
      value = value?.[k];
    }
    return value;
  };

  return (
    <Sheet
      variant="outlined"
      className={cn('overflow-auto rounded-lg', className)}
    >
      <JoyTable
        component="table"
        stripe={striped ? 'odd' : 'even'}
        hoverRow={hover}
        stickyHeader={stickyHeader}
        className="w-full"
      >
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={String(column.key)}
                style={{ width: column.width }}
                className={cn(
                  'px-4 py-3 font-semibold text-sm',
                  column.align === 'center' && 'text-center',
                  column.align === 'right' && 'text-right'
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-8">
                <div className="flex justify-center items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
                  <span>Loading...</span>
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-8 text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((column) => (
                  <td
                    key={String(column.key)}
                    className={cn(
                      'px-4 py-3',
                      column.align === 'center' && 'text-center',
                      column.align === 'right' && 'text-right'
                    )}
                  >
                    {column.render
                      ? column.render(getValue(row, String(column.key)), row)
                      : getValue(row, String(column.key))}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </JoyTable>
    </Sheet>
  );
}